import * as React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as s from "./DropdownMenu.style.css";
import type { DropdownMenuProps, CardinalDirection } from "./DropdownMenu.types";
import { MenuItem } from "./MenuItem";
import { MenuDivider } from "./MenuDivider";
import { getPortalContainer } from "../../lib/portalContainer";

export type { DropdownMenuProps } from "./DropdownMenu.types";
export { MenuItem } from "./MenuItem";
export { MenuDivider } from "./MenuDivider";

/**
 * Get the anchor point coordinates on the anchor element based on cardinal direction
 */
function getAnchorPoint(
  rect: DOMRect,
  direction: CardinalDirection
): { x: number; y: number } {
  switch (direction) {
    case "N":
      return { x: rect.left + rect.width / 2, y: rect.top };
    case "NE":
      return { x: rect.right, y: rect.top };
    case "E":
      return { x: rect.right, y: rect.top + rect.height / 2 };
    case "SE":
      return { x: rect.right, y: rect.bottom };
    case "S":
      return { x: rect.left + rect.width / 2, y: rect.bottom };
    case "SW":
      return { x: rect.left, y: rect.bottom };
    case "W":
      return { x: rect.left, y: rect.top + rect.height / 2 };
    case "NW":
      return { x: rect.left, y: rect.top };
    default:
      return { x: rect.left + rect.width / 2, y: rect.bottom };
  }
}

/**
 * Calculate menu position based on anchor point and open direction
 * Includes a small gap (4px) between anchor and menu for visual spacing
 */
function calculateMenuPosition(
  anchorX: number,
  anchorY: number,
  menuWidth: number,
  menuHeight: number,
  direction: CardinalDirection
): { top: number; left: number } {
  const gap = 4; // Small gap between anchor and menu

  switch (direction) {
    case "N": // Menu opens up, bottom center aligns with anchor
      return {
        left: anchorX - menuWidth / 2,
        top: anchorY - menuHeight - gap,
      };
    case "NE": // Menu opens up and right, bottom left aligns with anchor
      return {
        left: anchorX + gap,
        top: anchorY - menuHeight - gap,
      };
    case "E": // Menu opens right, left center aligns with anchor
      return {
        left: anchorX + gap,
        top: anchorY - menuHeight / 2,
      };
    case "SE": // Menu opens down and right, top left aligns with anchor
      return {
        left: anchorX + gap,
        top: anchorY + gap,
      };
    case "S": // Menu opens down, top center aligns with anchor
      return {
        left: anchorX - menuWidth / 2,
        top: anchorY + gap,
      };
    case "SW": // Menu opens down and left, top right aligns with anchor
      return {
        left: anchorX - menuWidth - gap,
        top: anchorY + gap,
      };
    case "W": // Menu opens left, right center aligns with anchor
      return {
        left: anchorX - menuWidth - gap,
        top: anchorY - menuHeight / 2,
      };
    case "NW": // Menu opens up and left, bottom right aligns with anchor
      return {
        left: anchorX - menuWidth - gap,
        top: anchorY - menuHeight - gap,
      };
    default:
      return {
        left: anchorX - menuWidth / 2,
        top: anchorY + gap,
      };
  }
}

export const DropdownMenu = ({
  children,
  anchorEl,
  anchorPos = "SE",
  openDirection = "SW",
  value,
  onChange,
  open,
  setOpen,
  closeOnSelect = true,
}: DropdownMenuProps) => {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

  // Calculate position when menu opens or anchor changes
  React.useEffect(() => {
    if (!open || !anchorEl) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchorElement = anchorEl instanceof HTMLElement ? anchorEl : anchorEl.current;
      if (!anchorElement) {
        setPosition(null);
        return;
      }

      const anchorRect = anchorElement.getBoundingClientRect();
      const calculatedAnchorPoint = getAnchorPoint(anchorRect, anchorPos);

      // Get menu dimensions - wait for render if needed
      if (!menuRef.current) {
        // Schedule update after render
        requestAnimationFrame(updatePosition);
        return;
      }

      const menuWidth = menuRef.current.offsetWidth;
      const menuHeight = menuRef.current.offsetHeight;

      let menuPosition = calculateMenuPosition(
        calculatedAnchorPoint.x,
        calculatedAnchorPoint.y,
        menuWidth,
        menuHeight,
        openDirection
      );

      // Constrain to viewport - ensure menu doesn't go off screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 8; // Padding from viewport edges

      // Adjust horizontal position to align with right edge if needed
      if (menuPosition.left + menuWidth > viewportWidth - padding) {
        menuPosition.left = viewportWidth - menuWidth - padding;
      }
      if (menuPosition.left < padding) {
        menuPosition.left = padding;
      }

      // Adjust vertical position to stay within viewport
      if (menuPosition.top + menuHeight > viewportHeight - padding) {
        menuPosition.top = viewportHeight - menuHeight - padding;
      }
      if (menuPosition.top < padding) {
        menuPosition.top = padding;
      }

      // Use getBoundingClientRect coordinates directly (already relative to viewport)
      setPosition({
        top: menuPosition.top,
        left: menuPosition.left,
      });
    };

    // Initial calculation - wait for next frame to ensure menu is rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(updatePosition);
    });

    // Update on scroll/resize
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, anchorEl, anchorPos, openDirection]);

  // Handle Escape key
  React.useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, setOpen]);

  // Handle click outside
  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (anchorEl == null) return;
      const anchorElement = anchorEl instanceof HTMLElement ? anchorEl : anchorEl.current;
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        anchorElement &&
        !anchorElement.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    // Use capture phase to catch clicks before they bubble
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [open, anchorEl, setOpen]);

  // Animation variants (similar to Modal)
  const menuVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: [0.16, 1, 0.3, 1],
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.15,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  // Clone children to pass context (skip MenuDivider - render as-is)
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type !== MenuDivider) {
      return React.cloneElement(child, {
        selected: child.props.value === value,
        onClick: child.props.disabled
          ? undefined
          : (e?: React.MouseEvent) => {
              e?.stopPropagation?.();
              if (onChange && child.props.value !== undefined) {
                onChange(child.props.value);
              }
              if (closeOnSelect) {
                setOpen(false);
              }
            },
      });
    }
    return child;
  });

  const menuContent = (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          ref={menuRef}
          className={s.dropdownMenu}
          style={
            position
              ? {
                  position: "fixed",
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                }
              : { position: "fixed", visibility: "hidden" }
          }
          variants={menuVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="menu"
          aria-orientation="vertical"
        >
          {childrenWithProps}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(menuContent, getPortalContainer());
};

